<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { LabelListV2, SliderV2 } from '@Shelly/shelly-design-system';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { capFirstChar, sortOn } from 'src/helpers/utilities';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Components
	import DirectionControlButtons from 'src/components/XT1/WindowCover/DirectionControlButtons.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { VCInterfaceStatus, VirtualComponentInterfaceData } from 'src/types/VirtualComponents';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	export let device: Device,
		statuses: VCInterfaceStatus[],
		showMarks = true;

	let isLoading: boolean = false;
	let valueBeforeSend: number = null;
	let statusPosition: VirtualComponentInterfaceData;
	let value: number = 0;

	const favoritesToNumbers = (fav: string) => {
		return fav.split(',').filter(Boolean).map(Number);
	};
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;
	$: marks = favoritesToNumbers(settings?.sys?.ui_data?.position || '').map((fav) => {
		return { value: fav, ariaLabel: fav + '%' };
	});

	const sendFavorites = async (favorites: number[]) => {
		const position = sortOn(favorites).toString();
		const params = { config: { ui_data: { position } } };
		const res = await gen2Request(device, 'Sys.SetConfig', params);
		onSettingsResponse(res, device);
	};

	const handleAddMark = (value: number) => {
		marks = [
			...marks,
			{
				value,
				ariaLabel: 'Saved mark at ' + value,
			},
		];
		const favorites = marks.map((mark) => mark.value);
		sendFavorites(favorites);
	};

	const handleDeleteMark = (indexToDelete: number) => {
		marks = marks.filter((_, index) => index !== indexToDelete);
		const favorites = marks.map((mark) => mark.value);
		sendFavorites(favorites);
	};

	$: if (statuses) {
		statusPosition = statuses.find((i) => /^(position)$/.test(i.role));
		value = valueBeforeSend !== null ? valueBeforeSend : statusPosition?.event || 0;
		if (!isLoading && valueBeforeSend !== null && statusPosition?.event == valueBeforeSend) {
			valueBeforeSend = null;
		}
	}

	const changePosition = async (value: number) => {
		isLoading = true;
		valueBeforeSend = value;
		const res: SettingRequest = await gen2Request(device, 'Number.set', {
			id: statusPosition.id,
			value,
		});
		if (!res?.isok) {
			valueBeforeSend = null;
			onSettingsResponse(res, device);
		}
		isLoading = false;
	};

	$: sensorStatuses = statuses.filter((i) => i?.sensor);
</script>

<div class="flex-full-height justify-content-end">
	<DeviceLabelList labelList={sensorStatuses} />
	<DeviceNotificationLabel deviceId={device?.id} />

	{#if statusPosition?.id}
		<SliderV2
			id="slider-windows-cover-position"
			a11yProps={{
				ariaLabel: 'Slider',
				ariaLabelValue: 'Value of slider',
				ariaLabelMultipleMarks:
					'Multiple marks. Press the Context Menu key, Shift+F10, or Control+Enter to open additional options.',
				ariaLabelMarkersMenu: 'Multiple marks menu',
				ariaLabelSavetMarkBtn: 'Save position as mark',
				ariaLabelDeleteMarkBtn: 'Delete mark position',
			}}
			class="flex-full-height mb-l"
			title={value == 0
				? capFirstChar($format(`slider_title_state_closing`, { default: 'closing' }))
				: capFirstChar($format('slider_title_state_opened', { default: 'opened' }))}
			{value}
			{isLoading}
			haveMarks={showMarks && settings ? true : false}
			marks={showMarks && settings ? marks : undefined}
			step={Number(statusPosition?.step || 1)}
			on:value_change={(e) => {
				changePosition(e.detail.value);
			}}
			unit={statusPosition.unit || ''}
			on:new_mark={(e) => {
				handleAddMark(e.detail.value);
			}}
			on:delete_mark={(e) => {
				handleDeleteMark(e.detail.index);
			}}
		/>
	{/if}

	<DirectionControlButtons {device} {statuses} />
</div>
